import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasAtLeastRole, type UpdateReconciliationTypeInput } from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const CLE = ['reconciliation-types'] as const;

/**
 * Types de rapprochement manuel (règle 17).
 *
 * Le type dit **pourquoi** deux mouvements ont été rapprochés à la main. C'est
 * la seule chose qui rende un rapprochement relisible six mois plus tard, et
 * c'est aussi ce qui permettra, plus tard, de voir qu'un même motif revient
 * toutes les semaines — et mérite alors une règle automatique plutôt qu'un
 * geste répété.
 *
 * **Désactiver n'efface rien.** Un type retiré disparaît de la liste de saisie ;
 * les rapprochements déjà enregistrés gardent le leur.
 *
 * « Propose la validation » (lot 0.14.0, type DAI) : après un rapprochement de
 * ce type, l'outil propose une validation temporaire pré-remplie, et une ligne
 * seule peut être rapprochée. ADMIN seul, audité.
 */
export function ReconciliationTypesPage(): ReactElement {
  const { user } = useAuth();
  const peutModifier = user ? hasAtLeastRole(user.role, 'ADMIN') : false;
  const queryClient = useQueryClient();
  const [libelle, setLibelle] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const types = useQuery({ queryKey: CLE, queryFn: () => api.reconciliationTypes(false) });

  const rafraichir = (texte: string): void => {
    setMessage(texte);
    setErreur(null);
    void queryClient.invalidateQueries({ queryKey: CLE });
  };

  const creation = useMutation({
    mutationFn: (valeur: string) => api.createReconciliationType({ libelle: valeur }),
    onSuccess: (type) => {
      setLibelle('');
      rafraichir(`Type « ${type.libelle} » créé.`);
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const modification = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateReconciliationTypeInput }) =>
      api.updateReconciliationType(id, body),
    onSuccess: (type, { body }) =>
      rafraichir(
        body.proposeValidation !== undefined
          ? `Type « ${type.libelle} » : ${type.proposeValidation ? 'propose désormais' : 'ne propose plus'} la validation après un rapprochement.`
          : type.actif
            ? `Type « ${type.libelle} » remis dans la liste de saisie.`
            : `Type « ${type.libelle} » retiré de la liste de saisie ; ses ${type.utilisations ?? 0} rapprochement(s) sont conservés.`,
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  function creer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (libelle.trim() === '') {
      setErreur('Le libellé ne peut pas être vide.');
      return;
    }
    creation.mutate(libelle.trim());
  }

  const enCours = creation.isPending || modification.isPending;

  return (
    <>
      <h1>Référentiels &mdash; Types de rapprochement</h1>
      <p className="sous-titre">
        Le motif d&apos;un rapprochement manuel. Rapprocher <strong>explique</strong> — cela ne
        solde pas : les cumuls et les indicateurs ne bougent pas.
      </p>

      {message && (
        <p className="alerte alerte--info" role="status">
          {message}
        </p>
      )}
      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      {peutModifier && (
        <div className="carte">
          <form className="barre-actions" onSubmit={creer}>
            <label className="champ champ--large" htmlFor="rappro-type-libelle">
              <span>Nouveau type</span>
              <input
                id="rappro-type-libelle"
                type="text"
                maxLength={60}
                placeholder="Par exemple : Reliquat d'expédition"
                value={libelle}
                onChange={(event) => setLibelle(event.target.value)}
              />
            </label>
            <button type="submit" className="bouton" disabled={enCours}>
              Créer
            </button>
          </form>
        </div>
      )}

      <div className="carte">
        {types.isPending && <p className="chargement">Chargement des types…</p>}
        {types.isError && (
          <p className="alerte alerte--erreur" role="alert">
            {messageErreur(types.error)}
          </p>
        )}

        {types.data && (
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Libellé</th>
                <th scope="col">Dans la liste de saisie</th>
                <th
                  scope="col"
                  title="Après un rapprochement de ce type, l'outil propose une validation temporaire pré-remplie ; une ligne seule peut être rapprochée."
                >
                  Propose la validation
                </th>
                <th scope="col">Rapprochements</th>
                {peutModifier && <th scope="col">Action</th>}
              </tr>
            </thead>
            <tbody>
              {types.data.map((type) => (
                <tr key={type.id}>
                  <td>{type.libelle}</td>
                  <td>
                    <span
                      className={`etiquette ${type.actif ? 'etiquette--actif' : 'etiquette--inactif'}`}
                    >
                      {type.actif ? 'Proposé' : 'Retiré'}
                    </span>
                  </td>
                  <td>
                    {peutModifier ? (
                      <input
                        type="checkbox"
                        checked={type.proposeValidation}
                        disabled={enCours}
                        aria-label={`Propose la validation — ${type.libelle}`}
                        onChange={() =>
                          modification.mutate({
                            id: type.id,
                            body: { proposeValidation: !type.proposeValidation },
                          })
                        }
                      />
                    ) : type.proposeValidation ? (
                      'Oui'
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="droite">{(type.utilisations ?? 0).toLocaleString('fr-FR')}</td>
                  {peutModifier && (
                    <td>
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        disabled={enCours}
                        onClick={() =>
                          modification.mutate({ id: type.id, body: { actif: !type.actif } })
                        }
                      >
                        {type.actif ? 'Retirer de la saisie' : 'Remettre dans la saisie'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
